import { useState, useEffect } from 'react';
import { logEvent } from '@amplitude/analytics-browser';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import SettingsIcon from '@mui/icons-material/Settings';
import Layout from '../../../componentsv2/Layout';
import Modal from '../../../componentsv2/Modal';
import MultiSelectField from '../../../componentsv2/Field/MultiSelectField';
import Typography from '../../../componentsv2/Typography';
import Section from '../../../componentsv2/Section';
import InfoButton from '../../../componentsv2/InfoButton';
import NumericText from '../../../componentsv2/NumericText';
import Button from '../../../componentsv2/Button';
import PageHeader from '../../../componentsv2/PageHeader';
import Tooltip from '../../../componentsv2/Tooltip';
import { useAppSelector } from '../../../store';
import { useSnackbar } from '../../../context/snackbar';
import { useTranslation } from '../../../hooks/i18n';
import { useUserLocation } from '../../../hooks/useUserLocation';
import { useRecentTransactions } from '../../../hooks/useRecentTransactions';
import { useBankingDetails, usePendingRfis, useUpdateGlobalAccountSettings } from '../../../hooks/useBanking';
import Banner, { BannerType } from '../../../componentsv2/Banner';
import RFIActionRequiredBanner from '../../../componentsv2/RFIActionRequiredBanner';
import { formatDate } from '../../../utils/common';
import { formatBankingDetails, AggregatedInfo } from '../../../utils/banking';
import { BANKING_CREATE_TRANSFER, BANKING_NEW_CONVERSION } from '../../../constants/auth';
import { bankingEvents } from '../../../constants/events';
import { UserRole } from '../../../types/companyUser';
import { COLORS } from '../../../themev2';
import AddFundsDrawer from './AddFundsDrawer';
import AccountBalancesTable from './AccountBalancesTable';
import RecentTransactionsTable from './RecentTransactionsTable';
import LockRateBanner from './LockRateBanner';
import { AddFundsIcon, ConvertIcon, TransferIcon } from './icons';

export default function BankingDashboard({ companyId, accountId }: { companyId: number; accountId: number }) {
  const navigate = useNavigate();
  const { t } = useTranslation(['banking', 'banking-page', 'common']);
  const { showSnackbar } = useSnackbar();
  const { language } = useUserLocation();
  const [showAddFundsDrawer, setShowAddFundsDrawer] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>(['USD', 'MXN', 'EUR']);
  const [tempSelectedCurrencies, setTempSelectedCurrencies] = useState<string[]>(['USD', 'MXN', 'EUR']);
  const isVisualizer = useAppSelector(
    (state) => state.companyUser.companyUser?.selectedCompanyRole === UserRole.VISUALIZER
  );

  const { mutate: updateSettings, isLoading: isSaving } = useUpdateGlobalAccountSettings({
    companyId,
    accountId,
  });

  const { data: recentTransactions = [], isLoading: isLoadingRecentTransactions } = useRecentTransactions({
    companyId,
    accountId,
  });

  const { data: bankingInfo, isLoading: isLoadingBankingInfo } = useBankingDetails({
    companyId,
    accountId,
  });

  const { data: pendingRfis } = usePendingRfis({ companyId, accountId });

  const isDebitsBlocked = !!bankingInfo?.debitsBlockedAt;

  const bankingDetails: AggregatedInfo = bankingInfo
    ? formatBankingDetails(bankingInfo)
    : ({} as AggregatedInfo);

  useEffect(() => {
    logEvent(bankingEvents.VIEW_DASHBOARD);
  }, []);

  // Set default currencies from bankingInfo or use defaults
  useEffect(() => {
    if (
      bankingInfo?.globalAccountListSettings &&
      bankingInfo.globalAccountListSettings.settings.globalAccountCurrencies
    ) {
      setSelectedCurrencies(bankingInfo.globalAccountListSettings.settings.globalAccountCurrencies);
      setTempSelectedCurrencies(bankingInfo.globalAccountListSettings.settings.globalAccountCurrencies);
    }
  }, [bankingInfo]);

  const saveSettings = () => {
    if (tempSelectedCurrencies.length !== 3) {
      return;
    }

    updateSettings(
      { globalAccountCurrencies: tempSelectedCurrencies },
      {
        onSuccess: () => {
          setSelectedCurrencies(tempSelectedCurrencies);
          setShowSettingsModal(false);
        },
        onError: () => {
          showSnackbar(t('banking-page:globalAccounts.settingsSaveError'), 'error');
        },
      }
    );
  };

  const handleModalClose = () => {
    setTempSelectedCurrencies(selectedCurrencies);
    setShowSettingsModal(false);
  };

  return (
    <Layout>
      <Grid container>
        <Grid item xs={6} md={9}>
          <PageHeader title={t('banking-page:globalAccount')} />
        </Grid>
        <Grid item xs={6} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          <Typography variant="body1">{formatDate(new Date(), language)}</Typography>
        </Grid>

        <RFIActionRequiredBanner rfis={pendingRfis} />

        {isDebitsBlocked && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Banner
              type={BannerType.Warning}
              title={t('banking-page:debitsBlocked.banner.title')}
              description={t('banking-page:debitsBlocked.banner.description')}
            />
          </Grid>
        )}

        <LockRateBanner t={t} />

        <Grid item xs={12} md={6} lg={7}>
          <Section isFullHeight sx={{ background: COLORS.secondary.neutralGreenGradient }}>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: COLORS.brand.white, fontWeight: 700 }}>
                  {t('banking-page:dashboard.totalBalance')}
                  <InfoButton title={t('banking-page:dashboard.totalBalanceTooltip')} color="secondary" />
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ marginTop: '1em', display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                <Typography sx={{ color: '#6DCEEF', fontWeight: 600 }}>
                  {t('banking-page:dashboard.dollars')}
                </Typography>
                {isLoadingBankingInfo ? (
                  <Skeleton
                    variant="rectangular"
                    height={40}
                    width={200}
                    animation="wave"
                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px' }}
                  />
                ) : (
                  <NumericText
                    currency="USD"
                    sx={{ color: COLORS.brand.white, fontWeight: 600, fontSize: '1.5em' }}
                  >
                    {bankingDetails.totalBalance || 0}
                  </NumericText>
                )}
              </Grid>
            </Grid>
          </Section>
        </Grid>
        <Grid item xs={12} md={6} lg={5}>
          <Section
            isFullHeight
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '24px',
              zIndex: 2,
            }}
          >
            {isVisualizer && (
              <Tooltip title={t('common:visualizerRole.actionNotAllowed')}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(228, 238, 252, 0.5)',
                    zIndex: 1,
                    borderRadius: 'inherit',
                  }}
                ></Box>
              </Tooltip>
            )}
            <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
              {t('banking-page:dashboard.actionsTitle')}
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                width: '85%',
                alignItems: 'center',
              }}
            >
              <Box
                onClick={() => {
                  if (isDebitsBlocked) return;
                  logEvent(bankingEvents.OPEN_CREATE_TRANSFER);
                  navigate(BANKING_CREATE_TRANSFER);
                }}
                sx={{
                  cursor: isDebitsBlocked ? 'not-allowed' : 'pointer',
                  opacity: isDebitsBlocked ? 0.5 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <TransferIcon />
                <Typography>{t('banking-page:actions.transfer')}</Typography>
              </Box>
              <Box
                onClick={() => {
                  if (isDebitsBlocked) return;
                  logEvent(bankingEvents.OPEN_CREATE_CONVERSION);
                  navigate(BANKING_NEW_CONVERSION);
                }}
                sx={{
                  cursor: isDebitsBlocked ? 'not-allowed' : 'pointer',
                  opacity: isDebitsBlocked ? 0.5 : 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <ConvertIcon />
                <Typography>{t('banking-page:actions.convert')}</Typography>
              </Box>
              <Box
                onClick={() => {
                  setShowAddFundsDrawer(true);
                  logEvent(bankingEvents.OPEN_ADD_FUNDS);
                }}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <AddFundsIcon />
                <Typography>{t('banking-page:actions.addFunds')}</Typography>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 1 } }}>
          <RecentTransactionsTable
            transactions={recentTransactions}
            isLoading={isLoadingRecentTransactions}
            language={language}
            t={t}
          />
        </Grid>
        <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 2 } }}>
          <Section
            sx={{
              padding: '1.5em',
              background: '#E4EEFC',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={10}>
                {isLoadingBankingInfo ? (
                  <Skeleton variant="text" width={200} height={28} sx={{ bgcolor: 'rgba(0, 0, 0, 0.1)' }} />
                ) : (
                  <Typography sx={{ fontWeight: 600, color: COLORS.gray[1400], fontSize: '18px' }}>
                    {t('banking-page:globalAccounts.title')}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box
                  sx={{
                    cursor: 'pointer',
                    color: COLORS.gray[1000],
                    '&:hover': { color: COLORS.gray[1400] },
                  }}
                  onClick={() => setShowSettingsModal(true)}
                >
                  <SettingsIcon />
                </Box>
              </Grid>
            </Grid>
          </Section>
          <AccountBalancesTable
            accountBalances={bankingInfo?.accountBalances ?? []}
            selectedCurrencies={selectedCurrencies}
            isLoading={isLoadingBankingInfo}
            t={t}
          />
        </Grid>
      </Grid>
      {bankingInfo && (
        <AddFundsDrawer
          show={showAddFundsDrawer}
          handleClose={() => {
            setShowAddFundsDrawer(false);
          }}
          t={t}
          connectedAccount={bankingInfo}
        />
      )}
      <Modal isOpen={showSettingsModal} handleClose={handleModalClose} isAutoHeight>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {t('banking-page:globalAccounts.settingsTitle')}
          </Typography>
          <Divider sx={{ mb: 2, borderColor: '#E4EEFC' }} />

          <MultiSelectField
            name="currencies"
            label={t('banking-page:globalAccounts.currenciesLabel')}
            options={
              bankingInfo?.accountBalances?.map(({ currency }) => ({
                value: currency,
                label: `${currency} - ${t(`banking-page:currencies.${currency}`)}`,
              })) || []
            }
            value={tempSelectedCurrencies}
            onChange={(value) => {
              setTempSelectedCurrencies(value);
            }}
            helperText={t('banking-page:globalAccounts.selectThreeCurrencies')}
            maxSelections={3}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
            <Button type="outline" color="tertiary" onClick={handleModalClose}>
              {t('common:cancel')}
            </Button>
            <Button
              onClick={saveSettings}
              isLoading={isSaving}
              disabled={tempSelectedCurrencies.length !== 3}
            >
              {t('common:save')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Layout>
  );
}
